import { useEffect, useState } from "react";
import { channels, stickyAddress } from "@/mocks/home";

type Channel = (typeof channels)[number];

function statusClasses(type: string) {
  return type === "ok"
    ? "bg-primary-100 text-primary-700"
    : "bg-accent-100 text-accent-800";
}

export default function EntryChannels() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!copiedId) return;
    const timer = window.setTimeout(() => setCopiedId(null), 1800);
    return () => window.clearTimeout(timer);
  }, [copiedId]);

  const handleCopy = async (channel: Channel) => {
    try {
      await navigator.clipboard.writeText(channel.host);
    } catch {
      // ignore clipboard failures
    }
    setCopiedId(channel.id);
  };

  const visible = showAll ? channels : channels.slice(0, 6);

  return (
    <section id="channels" className="border-y border-background-200 bg-background-100">
      <div className="mx-auto max-w-shell px-4 py-16 md:px-6 md:py-24">
        <div className="reveal flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-[11px] tracking-[0.3em] text-primary-600">
              ACCESS CHANNELS
            </span>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[42px]">
              入口通道：一处收齐主站、备用与镜像
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-7 text-foreground-600">
            每条通道独立维护、状态实时标注。主站无法访问时，直接在这里切换黑料总站备用入口即可。
          </p>
        </div>

        <div className="reveal mt-10 overflow-hidden rounded-lg border border-accent-300/70 bg-accent-50">
          <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-7">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-anchor-line text-2xl" />
              </span>
              <div>
                <p className="font-label text-[11px] tracking-[0.28em] text-accent-800">
                  {stickyAddress.label}
                </p>
                <p className="mt-2 font-heading text-2xl font-bold text-foreground-950">
                  {stickyAddress.host}
                </p>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-700">
                  {stickyAddress.hint}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(stickyAddress.host);
                } catch {
                  // ignore
                }
                setCopiedId("forever");
              }}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-3 font-label text-sm text-background-50 transition-colors hover:bg-foreground-800"
            >
              <i className={copiedId === "forever" ? "ri-check-line" : "ri-file-copy-line"} />
              {copiedId === "forever" ? "已复制" : "复制地址"}
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-3">
          {visible.map((channel) => (
            <article
              key={channel.id}
              className="reveal flex flex-col justify-between rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950">
                      {channel.name}
                    </h3>
                    <span className="mt-1 inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 font-label text-[10px] tracking-wider text-secondary-800">
                      {channel.tag}
                    </span>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 font-label text-[11px] ${statusClasses(
                      channel.statusType,
                    )}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        channel.statusType === "ok" ? "bg-primary-500" : "bg-accent-600"
                      }`}
                    />
                    {channel.status}
                  </span>
                </div>

                <p className="mt-5 break-all font-label text-sm text-foreground-800">
                  {channel.host}
                </p>
                <p className="mt-3 text-xs leading-6 text-foreground-600">{channel.note}</p>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-background-200 pt-4">
                <span className="font-label text-[11px] tracking-wider text-foreground-500">
                  延迟 {channel.latency}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopy(channel)}
                    className="flex h-9 items-center gap-1.5 rounded-md border border-background-300 px-3 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i
                      className={
                        copiedId === channel.id ? "ri-check-line" : "ri-file-copy-line"
                      }
                    />
                    {copiedId === channel.id ? "已复制" : "复制"}
                  </button>
                  <button
                    type="button"
                    className="flex h-9 items-center gap-1.5 rounded-md bg-primary-500 px-3 font-label text-xs text-background-50 transition-colors hover:bg-primary-600"
                  >
                    打开
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="reveal mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/20 px-5 py-3 font-label text-sm text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600"
          >
            <i className={showAll ? "ri-arrow-up-line" : "ri-list-check-2"} />
            {showAll ? "收起线路列表" : "查看全部线路与历史归档"}
          </button>
        </div>
      </div>
    </section>
  );
}